import { useCallback } from 'react';
import { message } from 'antd';
import { useDispatch } from 'react-redux';
import { ExitPreviewApi } from '@/apis/auth';
import { useSessionTransition } from '@/hooks';
import { useToken } from '@/common/helper';
import { PREVIEW_MODE_ROUTES } from '@/common/constants';
import { resetPreviewState } from '@/redux-toolkit/slice/previewSlice';

export interface ApiErrorResponse {
  data?: {
    message?: string;
    detail?: string;
  };
  message?: string;
}

export interface ExitPreviewOptions {
  /** Suppress the default "Exited preview mode successfully" toast, e.g.
   * when the caller (idle timeout) wants to show its own message instead. */
  silent?: boolean;
}

export interface UseExitPreviewReturn {
  /**
   * Ends the active preview session and returns the admin to the settings
   * screen the preview was launched from (Users List for "view as user",
   * Roles & Permissions for role preview), determined from the current
   * token's `preview_mode` claim.
   */
  exitPreview: (options?: ExitPreviewOptions) => Promise<boolean>;
}

/**
 * Shared Exit Preview logic used by both the manual "Exit" control in
 * PreviewHeader and the automatic 30-minute inactivity timeout
 * (usePreviewIdleTimeout), so both paths stay in sync.
 */
export const useExitPreview = (): UseExitPreviewReturn => {
  const dispatch = useDispatch();
  const { applyAuthSession } = useSessionTransition();
  const { getPreviewState } = useToken();

  const exitPreview = useCallback(
    async (options?: ExitPreviewOptions): Promise<boolean> => {
      // Read the active preview mode from the current token *before*
      // calling Exit -- once the session is exited, the token no longer
      // carries preview claims, so the return destination must be
      // captured up front.
      const { previewMode } = getPreviewState();
      const returnTo = previewMode
        ? PREVIEW_MODE_ROUTES[previewMode]
        : undefined;

      try {
        const res = await ExitPreviewApi();
        const resData = res?.data?.data;
        const isSuccess = resData?.is_success;
        const access = resData?.data?.access;
        const refresh = resData?.data?.refresh;

        if (isSuccess && access) {
          dispatch(resetPreviewState());
          const successMsg =
            resData?.message || 'Exited preview mode successfully';
          await applyAuthSession({
            access,
            refresh,
            successMessage: options?.silent ? undefined : successMsg,
            returnTo,
            mode: 'exitPreview',
          });
          return true;
        }

        const errorMsg = resData?.message || 'Failed to exit preview mode';
        message.error(errorMsg);
        return false;
      } catch (error: unknown) {
        const err = error as ApiErrorResponse;
        const errorMsg =
          err?.data?.message ||
          err?.data?.detail ||
          err?.message ||
          'Failed to exit preview mode';
        message.error(errorMsg);
        return false;
      }
    },
    [dispatch, applyAuthSession, getPreviewState]
  );

  return { exitPreview };
};

export default useExitPreview;
